<template>
  <b-card no-body class="tax-form-card mt-2">
    <div class="tax-section">
      <h6 class="section-label mt-0">
        <feather-icon icon="CreditCardIcon" size="13" class="mr-50" />Seller Deductions (Closing Statement)
      </h6>

      <div class="disb-subsection">
        <div class="disb-subtitle">Deductions from Seller Cheque</div>
        <draggable :value="sellerDeductions" @input="v => $emit('update:sellerDeductions', v)" handle=".ded-drag-handle" tag="div">
          <div v-for="(ded, index) in sellerDeductions" :key="ded.id" class="calc-row-group">
            <div class="custom-row">
              <feather-icon icon="MenuIcon" size="16" class="ded-drag-handle" />
              <b-form-input
                :value="ded.label"
                @input="v => updateDeduction(ded.id, { label: v })"
                placeholder="e.g. Rental (Prorated)"
                class="custom-row-label"
              />
              <b-input-group prepend="฿" class="custom-row-amount">
                <b-form-input
                  :value="formatNumber(ded.amount)"
                  type="text"
                  placeholder="0"
                  @input="v => updateDeduction(ded.id, { amount: parseSignedNumber(v) })"
                />
              </b-input-group>
              <b-button
                variant="outline-secondary"
                class="custom-row-remove"
                :class="{ active: ded.calc && ded.calc.open }"
                @click="toggleDeductionCalc(ded)"
              >
                <feather-icon icon="ToolIcon" size="16" />
              </b-button>
              <b-button
                variant="flat-danger"
                class="custom-row-remove"
                @click="removeSellerDeduction(index)"
              >
                <feather-icon icon="XIcon" size="16" />
              </b-button>
            </div>

            <div v-if="ded.calc && ded.calc.open" class="calc-panel">
              <div class="calc-field-row">
                <div class="calc-field">
                  <label class="calc-label">Period Start</label>
                  <thai-date-picker :value="ded.calc.periodStart" @input="v => updateDeductionCalc(ded.id, { periodStart: v })" />
                </div>
                <div class="calc-field">
                  <label class="calc-label">Period End</label>
                  <thai-date-picker :value="ded.calc.periodEnd" @input="v => updateDeductionCalc(ded.id, { periodEnd: v })" />
                </div>
              </div>

              <div class="calc-field-row">
                <div class="calc-field">
                  <label class="calc-label">Transfer Date</label>
                  <thai-date-picker :value="ded.calc.transferDate" @input="v => updateDeductionCalc(ded.id, { transferDate: v })" />
                </div>
                <div class="calc-field">
                  <label class="calc-label">Total Fee</label>
                  <b-input-group prepend="฿">
                    <b-form-input
                      :value="formatNumber(ded.calc.totalFee)"
                      type="text"
                      @input="v => updateDeductionCalc(ded.id, { totalFee: parseNumber(v) })"
                    />
                  </b-input-group>
                </div>
              </div>

              <div class="calc-summary">
                <div class="calc-line">
                  <span>Seller days / Buyer days</span>
                  <strong>{{ calcSellerDays(ded) }} / {{ calcBuyerDays(ded) }}</strong>
                </div>
                <div class="calc-line">
                  <span>Rate / day</span>
                  <strong>฿ {{ formatNumber(calcRatePerDay(ded)) }}</strong>
                </div>
                <div class="calc-line">
                  <span>Seller share / Buyer share</span>
                  <strong>฿ {{ formatNumber(calcSellerAmount(ded)) }} / ฿ {{ formatNumber(calcBuyerAmount(ded)) }}</strong>
                </div>
              </div>

              <b-button
                variant="outline-danger"
                size="sm"
                class="mt-50 mr-50"
                @click="applyDeductionCalc(ded, 'deduct')"
              >
                Apply −฿ {{ formatNumber(calcBuyerAmount(ded)) }} (deduct from Seller)
              </b-button>
              <b-button
                variant="outline-success"
                size="sm"
                class="mt-50"
                @click="applyDeductionCalc(ded, 'add')"
              >
                Apply +฿ {{ formatNumber(calcBuyerAmount(ded)) }} (add to Seller)
              </b-button>
            </div>
          </div>
        </draggable>
        <b-button variant="outline-secondary" size="sm" class="mt-50" @click="addSellerDeduction">
          <feather-icon icon="PlusIcon" size="13" class="mr-25" />Add Deduction
        </b-button>
        <small class="text-muted d-block mt-25">
          Type an amount directly (negative to deduct, positive to add back), or use
          <feather-icon icon="ToolIcon" size="11" /> to calculate a day-prorated amount.
        </small>

        <div class="disb-summary-line mt-50">
          <span>Total Deductions</span>
          <strong>฿ {{ formatNumber(sellerDeductionsTotal) }}</strong>
        </div>
        <div class="disb-summary-line disb-summary-line--total">
          <span>
            Net to the Seller
            <small class="text-muted">(Buyer total − Tax total + Deductions)</small>
          </span>
          <strong>฿ {{ formatNumber(sellerNetChequeTotal) }}</strong>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BCard, BFormInput, BInputGroup, BButton } from 'bootstrap-vue'
import draggable from 'vuedraggable'
import { formatNumber, parseNumber, parseSignedNumber } from '../format'
import {
  calcSellerDays, calcBuyerDays, calcRatePerDay, calcSellerAmount, calcBuyerAmount,
} from '../dedCalc'
import ThaiDatePicker from '../ThaiDatePicker.vue'

export default {
  components: {
    BCard, BFormInput, BInputGroup, BButton, draggable, ThaiDatePicker,
  },
  props: {
    sellerDeductions: { type: Array, required: true },
    sellerDeductionsTotal: { type: Number, default: 0 },
    sellerNetChequeTotal: { type: Number, default: 0 },
    transferDate: { type: String, default: null },
  },
  methods: {
    formatNumber,
    parseNumber,
    parseSignedNumber,
    calcSellerDays,
    calcBuyerDays,
    calcRatePerDay,
    calcSellerAmount,
    calcBuyerAmount,
    updateDeduction(id, patch) {
      const updated = this.sellerDeductions.map(d => (d.id === id ? { ...d, ...patch } : d))
      this.$emit('update:sellerDeductions', updated)
    },
    updateDeductionCalc(id, calcPatch) {
      const updated = this.sellerDeductions.map(d => (
        d.id === id ? { ...d, calc: { ...d.calc, ...calcPatch } } : d
      ))
      this.$emit('update:sellerDeductions', updated)
    },
    addSellerDeduction() {
      const updated = [...this.sellerDeductions, {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        label: '',
        amount: null,
        calc: null,
      }]
      this.$emit('update:sellerDeductions', updated)
    },
    removeSellerDeduction(index) {
      const updated = this.sellerDeductions.filter((_, i) => i !== index)
      this.$emit('update:sellerDeductions', updated)
    },
    toggleDeductionCalc(row) {
      const updated = this.sellerDeductions.map(d => {
        if (d.id !== row.id) return d
        if (!d.calc) {
          return {
            ...d,
            calc: {
              open: true, periodStart: null, periodEnd: null, transferDate: this.transferDate, totalFee: null,
            },
          }
        }
        return { ...d, calc: { ...d.calc, open: !d.calc.open } }
      })
      this.$emit('update:sellerDeductions', updated)
    },
    applyDeductionCalc(row, direction) {
      if (!row.calc) return
      const buyerAmount = calcBuyerAmount(row)
      const amount = direction === 'add' ? buyerAmount : -buyerAmount
      const updated = this.sellerDeductions.map(d => (
        d.id === row.id ? { ...d, amount, calc: { ...d.calc, open: false } } : d
      ))
      this.$emit('update:sellerDeductions', updated)
    },
  },
}
</script>
